import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { CalendarEvent, CaseFile, Note, LegalDocument, LegalPayment, SystemReport, UserData } from '../types';
import { db } from '../services/databaseService';
import { RateLimitService } from '../services/rateLimitService';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';

// Components
import CalendarView from './CalendarView';
import LegalChat from './LegalChat';
import AiEditorView from './AiEditorView';
import DocumentsView from './DocumentsView';
import PaymentsView from './PaymentsView';
import PerformanceView from './PerformanceView';
import HomeOverview from './HomeOverview';
import UserProfileModal from './UserProfileModal';
import LiveAssistant from './LiveAssistant';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';

interface DashboardProps {
  user: UserData;
  onLogout: () => void;
  onUpdateUser: (data: Partial<UserData>) => void;
  onAddReport: (report: Omit<SystemReport, 'id' | 'timestamp' | 'status'>) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout, onUpdateUser, onAddReport }) => {
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLiveAssistantOpen, setIsLiveAssistantOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<{title: string, msg: string} | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [cases, setCases] = useState<CaseFile[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [payments, setPayments] = useState<LegalPayment[]>([]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [casesData, eventsData, notesData, paymentsData, docsData] = await Promise.all([
        db.getCases(),
        db.getEvents(),
        db.getNotes(),
        db.getPayments(),
        db.getDocuments()
      ]);
      setCases(casesData || []);
      setEvents(eventsData || []);
      setNotes(notesData || []);
      setPayments(paymentsData || []);
      setDocuments(docsData || []);
    } catch (err) {
      console.error("Eroare la încărcarea datelor:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Event Handlers ──
  const handleAddEvent = async (e: CalendarEvent) => {
    try {
      const saved = await db.addEvent(e);
      setEvents(prev => [...prev, saved]);
    } catch (err) { console.error("Eroare adăugare eveniment:", err); }
  };
  const handleUpdateEvent = async (id: string, updates: Partial<CalendarEvent>) => {
    try {
      await db.updateEvent(id, updates);
      setEvents(prev => prev.map(e => e.id === id ? { ...e, ...updates } : e));
    } catch (err) { console.error("Eroare actualizare eveniment:", err); }
  };
  const handleDeleteEvent = async (id: string) => {
    try {
      await db.deleteEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (err) { console.error("Eroare ștergere eveniment:", err); }
  };

  // ── Case Handlers ──
  const handleAddCase = async (c: Partial<CaseFile>) => {
    try {
      const isAllowed = await RateLimitService.checkTotalLimit('cases', cases.length);
      if (!isAllowed) {
        toast.error("Limită Dosare", { description: "Ai atins limita maximă de dosare pentru contul tău." });
        return;
      }
      const saved = await db.addCase(c);
      if (saved) setCases(prev => [saved, ...prev]);
    } catch (err) { console.error("Eroare adăugare dosar:", err); }
  };
  const handleUpdateCase = async (id: string, updates: Partial<CaseFile>) => {
    try {
      const saved = await db.updateCase(id, updates);
      if (saved) setCases(prev => prev.map(c => c.id === id ? saved : c));
    } catch (err) { console.error("Eroare actualizare dosar:", err); }
  };
  const handleDeleteCase = async (id: string) => {
    try {
      const success = await db.deleteCase(id);
      if (success) setCases(prev => prev.filter(c => c.id !== id));
    } catch (err) { console.error("Eroare ștergere dosar:", err); }
  };

  // ── Note Handlers ──
  const handleAddNote = async (n: Omit<Note, 'id' | 'createdAt'>): Promise<Note | undefined> => {
    try {
      const saved = await db.addNote(n);
      setNotes(prev => [saved, ...prev]);
      return saved;
    } catch (err) { 
      console.error("Eroare adăugare notiță:", err);
      return undefined;
    }
  };
  const handleUpdateNote = async (id: string, updates: Partial<Note>) => {
    try {
      const updated = await db.updateNote(id, updates);
      setNotes(prev => prev.map(n => n.id === id ? updated : n));
    } catch (err) { console.error("Eroare actualizare notiță:", err); }
  };
  const handleDeleteNote = async (id: string) => {
    try {
      await db.deleteNote(id);
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch (err) { console.error("Eroare ștergere notiță:", err); }
  };

  // ── Payment Handlers ──
  const handleAddPayment = async (p: LegalPayment) => {
    try {
      const saved = await db.addPayment(p);
      setPayments(prev => [saved, ...prev]);
    } catch (err) { console.error("Eroare adăugare plată:", err); }
  };
  const handleUpdatePayment = async (id: string, updates: Partial<LegalPayment>) => {
    try {
      const saved = await db.updatePayment(id, updates);
      if (saved) setPayments(prev => prev.map(p => p.id === id ? saved : p));
    } catch (err) { console.error("Eroare actualizare plată:", err); }
  };
  const handleDeletePayment = async (id: string) => {
    try {
      await db.deletePayment(id);
      setPayments(prev => prev.filter(p => p.id !== id));
    } catch (err) { console.error("Eroare ștergere plată:", err); }
  };
  const handleTogglePaymentStatus = async (id: string) => {
    const payment = payments.find(p => p.id === id);
    if (!payment) return;
    const newStatus = payment.status === 'completed' ? 'pending' : 'completed';
    await handleUpdatePayment(id, { status: newStatus });
  };

  // ── Document Handlers ──
  const handleAddDocument = async (d: LegalDocument) => {
    setDocuments(prev => [d, ...prev]);
  };
  const handleDeleteDocument = async (id: string) => {
    try {
      await db.deleteDocument(id);
      setDocuments(prev => prev.filter(d => d.id !== id));
    } catch (err) { console.error("Eroare ștergere document:", err); }
  };
  const handleUpdateDocument = async (id: string, updates: Partial<LegalDocument>) => {
    setDocuments(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const hasPortalUpdates = cases.some(c => c.hasPortalUpdate);

  const checkLimit = async (type: 'case' | 'ai' | 'doc') => {
    let hit = false;
    let limitInfo = { title: '', msg: '' };

    if (type === 'case') {
      // Limită la nivel de număr de dosare a fost scoasă. Putem adăuga un check aici dacă revenim.
      hit = false;
    } else if (type === 'ai') {
      const isOk = await RateLimitService.checkDailyLimit('aiCalls');
      if (!isOk) {
        hit = true;
        limitInfo = RateLimitService.getLimitMessage('aiCalls');
      }
    } else if (type === 'doc') {
      // Calculăm teoretic stocarea curentă pentru un nou document (mock check)
      // Mai exact, acest check e făcut la momentul de upload efectiv în DocumentsView
      hit = false;
    }

    if (hit) {
      setShowUpgradeModal(limitInfo);
      return false;
    }
    return true;
  };

  const navItems = [
    { name: 'Home', path: '/dashboard', icon: '⚡' },
    { name: 'Calendar', path: '/dashboard/calendar', icon: '📅' },
    { name: 'Editor AI', path: '/dashboard/editor', icon: '✨' },
    { name: 'Docs', path: '/dashboard/documents', icon: '📂' },
    { name: 'Chat', path: '/dashboard/chat', icon: '🤖' },
  ];

  const userInitials = user.name.split(' ').filter(n => n !== 'Av.').map(n => n[0]).join('').toUpperCase();

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden w-full relative">
      <aside className={`w-72 bg-[#0F172A] text-white flex flex-col hidden md:flex shrink-0`}>
        <div className="p-8 border-b border-white/5 flex items-center gap-4">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center font-black shadow-lg shadow-indigo-500/20">L</div>
          <div>
            <h1 className="text-xl font-black tracking-tight italic leading-none">LexSmart</h1>
            <p className="text-[9px] text-slate-500 font-bold uppercase tracking-[0.2em] mt-1">Cabinet Digital</p>
          </div>
        </div>
        
        <nav className="flex-grow p-6 space-y-3 overflow-y-auto custom-scrollbar">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} className={`flex items-center gap-4 px-5 py-4 rounded-[1.25rem] transition-all group relative ${location.pathname === item.path ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <span className="text-xl group-hover:scale-110 transition-transform">{item.icon}</span>
              <span className="font-black text-[10px] uppercase tracking-widest">{item.name}</span>
            </Link>
          ))}
          
          {/* Buton Asistent Vocal în Sidebar pentru Desktop */}
          <button 
            onClick={() => setIsLiveAssistantOpen(true)}
            className="w-full flex items-center gap-4 px-5 py-4 rounded-[1.25rem] transition-all text-emerald-400 hover:text-white hover:bg-emerald-600/10 border border-emerald-500/10 mt-6"
          >
            <span className="text-xl">🎙️</span>
            <span className="font-black text-[10px] uppercase tracking-widest">Consult Vocal</span>
          </button>
          {user.email === 'madjo9@gmail.com' && (
            <button onClick={() => window.location.hash = '/admin'} className="w-full flex items-center gap-4 px-5 py-4 rounded-[1.25rem] transition-all text-amber-500 hover:bg-amber-500/10 mt-4">
              <Shield className="w-5 h-5" />
              <span className="font-black text-[10px] uppercase tracking-widest">Panou LexAdmin</span>
            </button>
          )}
        </nav>

        <div className="p-6 border-t border-white/5">
          <button onClick={onLogout} className="w-full text-left flex items-center gap-4 px-5 py-4 text-slate-400 hover:text-rose-400 rounded-xl transition-all font-black text-[9px] uppercase tracking-widest group">
            <span className="text-xl group-hover:scale-110">🚪</span> Deconectare Cont
          </button>
        </div>
      </aside>

      <main className={`flex-grow flex flex-col overflow-hidden relative pb-24 md:pb-0 w-full box-border`}>
        <header className="h-20 md:h-24 bg-white/80 backdrop-blur-md border-b border-slate-100 flex items-center justify-between px-4 sm:px-6 md:px-10 shrink-0 z-40 w-full box-border">
          <div className="flex items-center gap-4 min-w-0">
            <h2 className="text-sm sm:text-base md:text-2xl font-black text-slate-900 tracking-tight italic uppercase shrink-0">
              {navItems.find(item => item.path === location.pathname)?.name || 'LexSmart'}
            </h2>
          </div>
          <button onClick={() => setIsProfileOpen(true)} className="flex items-center gap-2 sm:gap-4 bg-white pl-3 sm:pl-6 pr-2 sm:pr-4 py-1 sm:py-2 rounded-full border border-slate-100 shadow-sm transition-all hover:shadow-xl group shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-[9px] sm:text-[10px] md:text-xs font-black text-slate-900 tracking-tight group-hover:text-indigo-600 uppercase">{user.name.split(' ').pop()}</p>
              <p className="text-[7px] sm:text-[8px] text-slate-400 font-bold uppercase tracking-widest">Profil</p>
            </div>
            <div className="w-9 h-9 sm:w-11 sm:h-11 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white font-black text-[9px] sm:text-[10px] md:text-sm shadow-xl border-2 sm:border-3 md:border-4 border-white">
              {userInitials}
            </div>
          </button>
        </header>

        <div className="flex-grow overflow-x-hidden overflow-y-auto bg-slate-50 custom-scrollbar w-full">
          <Routes>
            <Route path="/" element={<HomeOverview user={user} events={events} cases={cases} payments={payments} notes={notes} />} />
            <Route path="/calendar" element={<CalendarView events={events} cases={cases} onAddEvent={handleAddEvent} onUpdateEvent={handleUpdateEvent} onDeleteEvent={handleDeleteEvent} onAddCase={handleAddCase} onAddPayment={handleAddPayment} onUpdateCase={handleUpdateCase} />} />
            <Route path="/performance" element={<PerformanceView cases={cases} payments={payments} user={user} onAddCase={handleAddCase} onUpdateCase={handleUpdateCase} />} />
            <Route path="/editor" element={<AiEditorView notes={notes} cases={cases} user={user} onAddNote={handleAddNote} onUpdateNote={handleUpdateNote} onDeleteNote={handleDeleteNote} />} />
            <Route path="/documents" element={<DocumentsView user={user} documents={documents} cases={cases} onAddDocument={handleAddDocument} onDeleteDocument={handleDeleteDocument} onAddEvent={handleAddEvent} onUpdateDocument={handleUpdateDocument} />} />
            <Route path="/finance" element={<PaymentsView payments={payments} cases={cases} onAddPayment={handleAddPayment} onDeletePayment={handleDeletePayment} onUpdatePayment={handleUpdatePayment} onToggleStatus={handleTogglePaymentStatus} />} />
            <Route path="/chat" element={<LegalChat events={events} cases={cases} notes={notes} payments={payments} user={user} onUpdateUser={onUpdateUser} checkAiLimit={() => checkLimit('ai')} />} />
          </Routes>
        </div>

        {/* Buton plutitor pentru mobil (Floating Voice Action) */}
        <button 
          onClick={() => setIsLiveAssistantOpen(true)}
          className="md:hidden fixed bottom-28 right-6 w-16 h-16 bg-emerald-600 text-white rounded-full flex items-center justify-center shadow-2xl z-[150] animate-bounce"
        >
          <span className="text-3xl">🎙️</span>
        </button>

        <nav className="md:hidden fixed bottom-0 left-0 right-0 h-20 sm:h-22 bg-white/95 backdrop-blur-xl border-t border-slate-100 flex items-center justify-around px-2 z-[100] shadow-[0_-15px_40px_rgba(0,0,0,0.08)] w-full box-border">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} className={`flex-1 flex flex-col items-center justify-center gap-1 min-w-0 py-2 transition-all relative overflow-visible ${location.pathname === item.path ? 'text-indigo-600' : 'text-slate-400'}`}>
              <span className="text-xl sm:text-2xl transition-transform active:scale-90">{item.icon}</span>
              <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-tight whitespace-nowrap leading-none text-center w-full px-1">{item.name}</span>
            </Link>
          ))}
        </nav>
      </main>

      <LiveAssistant 
        isOpen={isLiveAssistantOpen} 
        onClose={() => setIsLiveAssistantOpen(false)} 
        cases={cases} 
        events={events}
        documents={documents}
        user={user}
      />

      {isProfileOpen && <UserProfileModal user={user} onClose={() => setIsProfileOpen(false)} onLogout={onLogout} onUpdateUser={onUpdateUser} />}

      {showUpgradeModal && (
        <div className="fixed inset-0 z-[500] bg-slate-900/90 backdrop-blur-xl flex items-center justify-center p-6 animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] p-10 shadow-2xl text-center space-y-6 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center text-3xl mx-auto">⚡</div>
            <h3 className="text-2xl font-black text-slate-900 italic tracking-tight">{showUpgradeModal.title}</h3>
            <p className="text-sm text-slate-500 font-medium italic leading-relaxed">{showUpgradeModal.msg}</p>
            <div className="flex gap-4 pt-4">
              <button onClick={() => setShowUpgradeModal(null)} className="w-full py-4 bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-200 hover:bg-indigo-700 active:scale-95 transition-all">Am Înțeles</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
